import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useUser } from './useUser';
import { useQuery } from '@apollo/client';
import { GET_ENTITY_SENTRY_TRACKING, CHECK_ENTITY_TRACKING } from '../components/sentry-tracking/sentryGraphQL';
import { logger } from '../utils/logger';

// Context for Sentry tracking
interface SentryContextType {
  isTracked: boolean;
  trackingLevel: string | null;
  trackingReason: string | null;
  isLoading: boolean;
  error: Error | null;
  refreshTracking: () => void;
}

const SentryContext = createContext<SentryContextType>({
  isTracked: false,
  trackingLevel: null,
  trackingReason: null,
  isLoading: false,
  error: null,
  refreshTracking: () => {},
});

// Global production sample rate - 3% as specified
const PRODUCTION_SAMPLE_RATE = 0.03;

// Sample rate based on region - for more granular control
const getRegionSampleRate = (country?: string): number => {
  if (import.meta.env.DEV) {
    return 0.1; // 10% in non-production environments
  }
  
  // In production, use region-specific rates
  switch (country?.toLowerCase()) {
    case 'australia':
    case 'au':
      return 0.05; // 5% of Australian users
    case 'canada':
    case 'ca':
      return 0.03; // 3% of Canadian users
    case 'usa':
    case 'us':
      return 0.03; // 3% of US users (matching the global rate)
    default:
      return 0.01; // 1% of users from other regions
  }
};

// Check if user should be sampled based on ID and region
const shouldSampleUser = (userId: string | undefined, country?: string): boolean => {
  if (!userId) return false;
  
  try {
    // Use user ID to deterministically sample users
    // This ensures the same users are consistently sampled
    const hash = userId.split('').reduce((acc, char) => {
      return (acc * 31 + char.charCodeAt(0)) & 0xFFFFFFFF;
    }, 0);
    
    const normalizedHash = hash / 0xFFFFFFFF; // Normalize to 0-1 range
    
    // Use global rate for production, or region-specific for more granular control
    const sampleRate = import.meta.env.PROD 
      ? PRODUCTION_SAMPLE_RATE 
      : getRegionSampleRate(country);
    
    return normalizedHash < sampleRate;
  } catch (error) {
    logger.error('Error in shouldSampleUser:', error);
    return false;
  }
};

// Check if a date is valid and not expired
const isDateValid = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  
  try {
    const date = new Date(dateStr);
    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};

// Check if tracking is active based on start and end dates
const isTrackingActive = (
  startDate: string | null | undefined, 
  endDate: string | null | undefined
): boolean => {
  try {
    const now = new Date();
    
    // Check start date
    if (!isDateValid(startDate)) return false;
    const start = new Date(startDate as string);
    if (now < start) return false;
    
    // Check end date if it exists
    if (isDateValid(endDate)) {
      const end = new Date(endDate as string);
      if (now > end) return false;
    }
    
    return true;
  } catch (error) {
    logger.error('Error checking tracking dates:', error);
    return false;
  }
};

// Store tracking configuration in session storage
const storeTrackingConfig = (
  isTracked: boolean,
  level: string | null,
  reason: string | null,
  entityType?: string | null,
  entityId?: string | null,
  entityName?: string | null,
  userEmail?: string | null,
  userId?: string | null,
  accountId?: string | null,
  accountName?: string | null,
  schoolId?: string | null,
  schoolName?: string | null,
) => {
  try {
    // Store complete tracking configuration
    const trackingConfig = {
      isTracked,
      trackingLevel: level,
      trackingReason: reason,
      entityType,
      entityId,
      entityName,
      entityEmail: userEmail || null, // Add email to the stored config
      userId: userId || entityId, // Use userId if provided, otherwise use entityId
      accountId,
      accountName,
      schoolId,
      schoolName,
      updatedAt: Date.now()
    };
    
    // Store as JSON string
    sessionStorage.setItem('sentry_tracking_config', JSON.stringify(trackingConfig));
    logger.info('[Sentry Tracking] Stored tracking config in session storage:', trackingConfig);
    
    // For backward compatibility, also store individual items
    if (isTracked && level) {
      sessionStorage.setItem('sentry_tracking_level', level);
      sessionStorage.setItem('sentry_tracking_updated_at', Date.now().toString());
    } else {
      sessionStorage.removeItem('sentry_tracking_level');
      sessionStorage.removeItem('sentry_tracking_updated_at');
    }
  } catch (error) {
    logger.error('Error storing tracking config in session storage:', error);
  }
};

// Retrieve tracking configuration from session storage
const getStoredTrackingConfig = () => {
  try {
    const storedConfig = sessionStorage.getItem('sentry_tracking_config');
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig);
        // Use console.log instead of console.warn to avoid triggering error boundary
        logger.info('[Sentry Tracking] Retrieved tracking config from session storage:', config);
        return config;
      } catch (parseError) {
        logger.error('Error parsing sentry_tracking_config:', parseError);
        // Remove invalid JSON to prevent future errors
        sessionStorage.removeItem('sentry_tracking_config');
      }
    }
    
    // Check for backward compatibility
    const level = sessionStorage.getItem('sentry_tracking_level');
    if (level) {
      const config = {
        isTracked: true,
        trackingLevel: level,
        trackingReason: 'stored',
        updatedAt: sessionStorage.getItem('sentry_tracking_updated_at') || Date.now()
      };
      logger.info('[Sentry Tracking] Retrieved legacy tracking config:', config);
      return config;
    }
    
    return null;
  } catch (error) {
    logger.error('Error retrieving tracking config from session storage:', error);
    return null;
  }
};

// CENTRALIZED FUNCTION: Configure Sentry based on tracking level
// This is the ONLY place in the entire application where Sentry.setUser should be called
const configureSentryForTrackingLevel = (
  level: string,
  userId: string,
  userEmail?: string,
  userName?: string,
  accountId?: string,
  accountName?: string,
  schoolId?: string,
  schoolName?: string,
  trackingReason?: string,
  entityType?: string,
  entityId?: string
) => {
  try {
    logger.info(`[Sentry Tracking] CENTRALIZED: Setting user data for ${userId} at ${level} level`);
    
    // Set user context - this is the ONLY place Sentry.setUser should be called
    // NOTE: Email is intentionally excluded for privacy - only user ID is set
    Sentry.setUser({
      id: String(userId),
      username: userName || undefined,
      ip_address: '{{auto}}',
    });
    
    // Add user metadata as tags for easier filtering
    Sentry.setTag('user_id', String(userId));
    // NOTE: Email intentionally excluded for privacy
    
    // Add account info if available
    if (accountId) {
      Sentry.setTag('account_id', String(accountId));
      if (accountName) Sentry.setTag('account_name', accountName);
    }
    
    // Add school info if available
    if (schoolId) {
      Sentry.setTag('school_id', String(schoolId));
      if (schoolName) Sentry.setTag('school_name', schoolName);
    }
    
    // Set tracking level and reason
    Sentry.setTag('tracking_level', level);
    if (trackingReason) {
      Sentry.setTag('tracking_reason', trackingReason);
    }
    
    // Set session ID for tracking
    Sentry.setTag('session_id', `session_${Date.now()}`);
    
    // Store tracking configuration in session storage
    storeTrackingConfig(
      true, 
      level, 
      trackingReason || null,
      entityType,
      entityId,
      entityType === 'user' ? userName : 
      entityType === 'account' ? accountName : 
      entityType === 'school' ? schoolName : null,
      userEmail,
      userId, // Pass the userId parameter
      accountId,
      accountName,
      schoolId,
      schoolName,
    );
    
    // Configure Sentry based on tracking level
    switch (level) {
      case 'basic':
        // Basic level - just errors
        Sentry.setTag('sentry_level', 'error');
        break;
        
      case 'detailed':
        // Detailed level - errors and warnings, more breadcrumbs
        Sentry.setTag('sentry_level', 'warning');
        
        // Set higher tracing sample rate for detailed tracking
        Sentry.setTag('traces_sample_rate', '0.5');
        
        // Note: We can't directly modify Sentry options after initialization
        // These tags will be used by the backend to determine how to process events
        break;
        
      case 'full':
        // Full level - all logs, session replay, detailed performance
        Sentry.setTag('sentry_level', 'info');
        
        // Set maximum tracing sample rate for full tracking
        Sentry.setTag('traces_sample_rate', '1.0');
        Sentry.setTag('replays_session_sample_rate', '1.0');
        Sentry.setTag('replays_on_error_sample_rate', '1.0');
        
        // Note: We can't directly modify Sentry options after initialization
        // These tags will be used by the backend to determine how to process events
        break;
        
      default:
        // Default to basic level
        Sentry.setTag('sentry_level', 'error');
        break;
    }
    
    logger.info(`[Sentry Tracking] Enabled for user ${userId} at ${level} level (${trackingReason || 'unspecified reason'})`);
    return true;
  } catch (error) {
    logger.error('Error configuring Sentry for tracking level:', error);
    return false;
  }
};

// Set up polling interval to periodically check tracking status
const POLLING_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds

// Global variable to track initialization across remounts
let isGloballyInitialized = false;

// Debounce helper to prevent rapid consecutive updates
let lastUpdateTime = 0;
const DEBOUNCE_DELAY = 2000; // 2 seconds

// Wrap SentryProvider in React.memo to prevent unnecessary re-renders
export const SentryProvider: React.FC<React.PropsWithChildren<{}>> = React.memo(({ children }) => {
  logger.info('[Sentry Tracking] SentryProvider rendering');
  // Use a ref to track initialization within this component instance
  const hasInitialized = useRef<boolean>(isGloballyInitialized);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef<boolean>(true);
  const userIdRef = useRef<string | null>(null);
  
  // Only log the first time
  if (!hasInitialized.current) {
    logger.info('[Sentry Tracking] SentryProvider component mounting for the first time');
    hasInitialized.current = true;
    isGloballyInitialized = true;
  }
  
  const [user, userQuery] = useUser();
  const userLoading = userQuery.loading;
  
  // Try to get stored tracking config
  const storedConfig = getStoredTrackingConfig();
  
  const [isTracked, setIsTracked] = useState<boolean>(storedConfig?.isTracked || false);
  const [trackingLevel, setTrackingLevel] = useState<string | null>(storedConfig?.trackingLevel || null);
  const [trackingReason, setTrackingReason] = useState<string | null>(storedConfig?.trackingReason || null);
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [error, setError] = useState<Error | null>(null);
  
  // If we have stored config, log it (no useEffect needed)
  if (storedConfig && isFirstRender.current) {
    logger.info('[Sentry Tracking] Initialized with stored config:', storedConfig);
  }
  
  // Query for user-specific tracking configuration
  // Use a more resilient approach that doesn't depend on the component hierarchy
  const { data: trackingData, loading: trackingLoading, refetch: refetchTracking, error: trackingError } = useQuery(
    GET_ENTITY_SENTRY_TRACKING,
    {
      variables: {
        entityId: user?.id || '',
        entityType: 'user'
      },
      skip: !user?.id,
      fetchPolicy: 'network-only',
      // Handle errors gracefully since we're now at the top level
      onError: (error) => {
        logger.info('[Sentry Tracking] Error fetching user tracking:', error.message);
        // Don't throw errors that would crash the app
      }
    }
  );
  
  // Query to check if user belongs to a tracked account or school
  // Note: This query might not be available in all backend environments
  const { data: entityTrackingData, loading: entityTrackingLoading, refetch: refetchEntityTracking, error: entityTrackingError } = useQuery(
    CHECK_ENTITY_TRACKING,
    {
      variables: {
        userId: user?.id || ''
      },
      skip: !user?.id,
      fetchPolicy: 'network-only',
      // Don't throw errors if the query doesn't exist
      onError: (error) => {
        logger.info('[Sentry Tracking] Error checking entity tracking (query might not exist):', error.message);
      }
    }
  );
  
  // Function to check if user should be tracked based on random sampling
  const checkRandomSampling = useCallback(() => {
    if (!user?.id) return false;
    
    // Use a default region if country is not available
    const shouldTrack = shouldSampleUser(user.id, 'unknown');
    
    return shouldTrack;
  }, [user]);
  
  // Check if user has specific tracking configuration
  const checkSpecificTracking = useCallback(() => {
    if (!user?.id) return false;
    
    logger.info('[Sentry Tracking] Checking specific tracking for user:', user.id);
    logger.info('[Sentry Tracking] Entity tracking data:', entityTrackingData?.checkEntityTracking);
    logger.info('[Sentry Tracking] User tracking data:', trackingData?.sentryTrackingByEntity);
    
    // Check if user belongs to a tracked account or school - this takes priority
    const entityTracking = entityTrackingData?.checkEntityTracking;
    if (entityTracking && entityTracking.isTracked) {
      logger.info('[Sentry Tracking] User belongs to tracked entity:', entityTracking);
      
      return {
        isTracked: true,
        trackingLevel: entityTracking.trackingLevel || 'basic',
        trackingReason: entityTracking.entityType === 'account' ? 'account_member' : 'school_member',
        entityType: entityTracking.entityType,
        entityId: entityTracking.entityId,
        entityName: entityTracking.entityName
      };
    }
    
    // Check if user is directly tracked
    const userTracking = trackingData?.sentryTrackingByEntity;
    if (userTracking && userTracking.isActive && isTrackingActive(userTracking.startDate, userTracking.endDate)) {
      logger.info('[Sentry Tracking] User is explicitly tracked:', userTracking);
      
      return {
        isTracked: true,
        trackingLevel: userTracking.trackingLevel || 'basic',
        trackingReason: 'explicit_user',
        entityType: 'user',
        entityId: user.id,
        entityName: `${user.firstName || ''} ${user.lastName || ''}`.trim()
      };
    }
    
    logger.info('[Sentry Tracking] No specific tracking found for user');
    return false;
  }, [user, trackingData, entityTrackingData]);
  
  // Refresh tracking data with debounce
  const refreshTracking = useCallback(() => {
    logger.info(`[Sentry Tracking] refreshTracking called, user: ${user?.id}`);
    
    if (!user?.id) return;
    
    // Apply debouncing to prevent rapid consecutive updates
    const now = Date.now();
    if (now - lastUpdateTime < DEBOUNCE_DELAY) {
      logger.info('[Sentry Tracking] Debouncing update - too soon after last update', {
        timeSinceLastUpdate: now - lastUpdateTime,
        debounceDelay: DEBOUNCE_DELAY
      });
      return;
    }
    
    // Update the last update time
    lastUpdateTime = now;
    
    // setIsLoading(true);
    // setError(null);
    
    try {
      // Combine both refetch operations into a single Promise.all
      // Use Promise.resolve(null) as a fallback if refetch function is not available
      Promise.all([
        refetchTracking ? refetchTracking() : Promise.resolve(null),
        refetchEntityTracking ? refetchEntityTracking() : Promise.resolve(null)
      ])
        .then(([userResult, entityResult]) => {
          logger.info('[Sentry Tracking] All tracking queries completed');
          
          // Use the refetched data directly instead of relying on cached data
          const userTracking = userResult?.data?.sentryTrackingByEntity;
          const entityTracking = entityResult?.data?.checkEntityTracking;
          
          logger.info('[Sentry Tracking] User tracking result:', userTracking);
          logger.info('[Sentry Tracking] Entity tracking result:', entityTracking);
          
          // Prepare common user data needed for Sentry configuration
          const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          const accountId = user.customer?.id;
          const accountName = user.customer?.name;
          const schoolId = user.schools && user.schools.length > 0 ? user.schools[0]?.id : undefined;
          const schoolName = user.schools && user.schools.length > 0 ? user.schools[0]?.name : undefined;
          
          // Define a type for tracking configuration
          type TrackingConfig = {
            isTracked: boolean;
            trackingLevel: string | null;
            trackingReason: string | null;
            entityType: string | null;
            entityId: string | null;
            entityName: string | null;
          };
          
          // Check for specific tracking first (entity or direct user tracking)
          // Initialize with default values
          let trackingConfig: TrackingConfig = {
            isTracked: false,
            trackingLevel: null,
            trackingReason: null,
            entityType: null,
            entityId: null,
            entityName: null
          };
          
          // Check if user belongs to a tracked entity (account or school)
          if (entityTracking?.isTracked) {
            trackingConfig = {
              isTracked: true,
              trackingLevel: entityTracking.trackingLevel || 'basic',
              trackingReason: entityTracking.entityType === 'account' ? 'account_member' : 'school_member',
              entityType: entityTracking.entityType,
              entityId: entityTracking.entityId,
              entityName: entityTracking.entityName
            };
            logger.info('[Sentry Tracking] User belongs to tracked entity:', trackingConfig);
          } 
          // Check if user is directly tracked
          else if (userTracking?.isActive && isTrackingActive(userTracking.startDate, userTracking.endDate)) {
            trackingConfig = {
              isTracked: true,
              trackingLevel: userTracking.trackingLevel || 'basic',
              trackingReason: 'explicit_user',
              entityType: 'user',
              entityId: user.id,
              entityName: userName
            };
            logger.info('[Sentry Tracking] User is explicitly tracked:', trackingConfig);
          }
          // Check random sampling as a fallback
          else {
            const shouldTrack = shouldSampleUser(user.id, 'unknown');
            
            if (shouldTrack) {
              // Use entity tracking level if available, otherwise default to basic
              const trackingLevel = entityTracking?.trackingLevel || 'basic';
              
              trackingConfig = {
                isTracked: true,
                trackingLevel: trackingLevel,
                trackingReason: 'random_sample',
                entityType: 'user',
                entityId: user.id,
                entityName: userName
              };
              logger.info('[Sentry Tracking] User is tracked via random sampling:', trackingConfig);
            } else {
              // Already initialized with default values
              logger.info('[Sentry Tracking] User is not tracked');
            }
          }
          
          // Only update state and configure Sentry if values have changed
          if (isTracked !== trackingConfig.isTracked || 
              trackingLevel !== trackingConfig.trackingLevel || 
              trackingReason !== trackingConfig.trackingReason) {
            
            logger.info('[Sentry Tracking] Updating state with new tracking configuration');
            setIsTracked(trackingConfig.isTracked);
            setTrackingLevel(trackingConfig.trackingLevel);
            setTrackingReason(trackingConfig.trackingReason);
            
            if (trackingConfig.isTracked && trackingConfig.trackingLevel) {
              // Configure Sentry with the tracking configuration
              configureSentryForTrackingLevel(
                trackingConfig.trackingLevel,
                user.id,
                user.email,
                userName,
                accountId,
                accountName,
                schoolId,
                schoolName,
                trackingConfig.trackingReason || undefined,
                trackingConfig.entityType || undefined,
                trackingConfig.entityId || undefined
              );
            } else {
              // Clear Sentry configuration if user should not be tracked
              Sentry.setUser(null);
              storeTrackingConfig(false, null, null);
            }
          } else {
            logger.info('[Sentry Tracking] Tracking configuration unchanged, skipping state update');
          }
          
          // setIsLoading(false);
        })
        .catch(error => {
          logger.error('Error processing Sentry tracking:', error);
          // setError(error instanceof Error ? error : new Error(String(error)));
          setIsTracked(false);
          setTrackingLevel(null);
          setTrackingReason(null);
          // setIsLoading(false);
        });
    } catch (error) {
      logger.error('Error processing Sentry tracking:', error);
      // setError(error instanceof Error ? error : new Error(String(error)));
      setIsTracked(false);
      setTrackingLevel(null);
      setTrackingReason(null);
      // setIsLoading(false);
    }
  }, [user, refetchTracking, refetchEntityTracking, isTracked, trackingLevel, trackingReason]);
  
  // Setup polling function - no useEffect needed
  const setupPolling = useCallback(() => {
    if (!user?.id) return;
    
    // Skip if we already have a polling timer for this user
    if (pollingTimerRef.current && userIdRef.current === user.id) {
      logger.info('[Sentry Tracking] Polling timer already exists for this user, skipping setup');
      return;
    }
    
    // Clear existing timer if it exists
    if (pollingTimerRef.current) {
      logger.info('[Sentry Tracking] Clearing existing polling timer');
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    
    // Set up new polling timer
    logger.info('[Sentry Tracking] Setting up new polling timer');
    pollingTimerRef.current = setInterval(() => {
      logger.info(`[Sentry Tracking] Polling backend for updates (every ${POLLING_INTERVAL/1000} seconds)...`);
      refreshTracking();
    }, POLLING_INTERVAL);
    
    // Return cleanup function
    return () => {
      if (pollingTimerRef.current) {
        logger.info('[Sentry Tracking] Cleaning up polling timer');
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [user?.id, refreshTracking]);
  
  // Cleanup function for component unmount
  React.useEffect(() => {
    return () => {
      if (pollingTimerRef.current) {
        logger.info('[Sentry Tracking] Component unmounting, cleaning up polling timer');
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);
  
  // Use useEffect for user changes to better control when they happen
  // This is critical now that we're at the top level of the app
  React.useEffect(() => {
    if (isFirstRender.current) {
      logger.info('[Sentry Tracking] First render, initializing');
      isFirstRender.current = false;
    }
    
    // If we have a user, refresh tracking and set up polling
    if (user?.id) {
      if (userIdRef.current !== user.id) {
        logger.info('[Sentry Tracking] User ID changed or became available:', user.id);
        userIdRef.current = user.id;
        
        // Use setTimeout to delay the tracking refresh slightly
        // This prevents it from competing with other initialization processes
        setTimeout(() => {
          refreshTracking();
          setupPolling();
        }, 500);
      }
    } else if (!user?.id && pollingTimerRef.current) {
      // If user becomes unavailable and we have a polling timer, clear it
      logger.info('[Sentry Tracking] User no longer available, clearing polling');
      userIdRef.current = null;
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
      
      // Clear tracking data
      setIsTracked(false);
      setTrackingLevel(null);
      setTrackingReason(null);
      
      // Clear session storage
      storeTrackingConfig(false, null, null);
      
      // Clear Sentry user context
      try {
        Sentry.setUser(null);
      } catch (error) {
        logger.error('Error clearing Sentry user context:', error);
      }
    }
  }, [refreshTracking, setupPolling, user?.id]);
  
  // Provide context value
  const contextValue: SentryContextType = {
    isTracked,
    trackingLevel,
    trackingReason,
    isLoading: trackingLoading || entityTrackingLoading || userLoading,
    error: trackingError || entityTrackingError || null,
    refreshTracking
  };
  
  return (
    <SentryContext.Provider value={contextValue}>
      {children}
    </SentryContext.Provider>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Always return true to prevent re-renders from prop changes
  // The component will still re-render if its internal state changes
  return true;
});

// Hook for using Sentry context
export const useSentry = () => {
  const context = useContext(SentryContext);
  
  if (!context) {
    throw new Error('useSentry must be used within a SentryProvider');
  }
  
  return context;
};
